import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createProductMetaDataAPI,
  deleteProductMetaDataAPI,
  deleteProductMetaDataIconAPI,
  getAllProductMetaDataAPI,
  getProductMetaDataAPI,
  updateProductMetaDataAPI,
  uploadProductMetaDataIconAPI,
} from "../../api/ecommerce/ProductMetaDataApi";

const initialState = {
  productMetaData: null,
  productMetaDataList: [],
  uploadedIcon: null,
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  iconUploading: false,
  iconDeleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (error) => ({
  errorMessage:
    error?.data?.message ||
    error?.response?.data?.message ||
    error?.data?.error ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong",
});

const createProductMetaDataThunk = (type, api) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorPayload(error));
    }
  });

export const getProductMetaData = createProductMetaDataThunk(
  "productMetaData/getProductMetaData",
  getProductMetaDataAPI,
);

export const getAllProductMetaData = createProductMetaDataThunk(
  "productMetaData/getAllProductMetaData",
  getAllProductMetaDataAPI,
);

export const createProductMetaData = createProductMetaDataThunk(
  "productMetaData/createProductMetaData",
  createProductMetaDataAPI,
);

export const updateProductMetaData = createProductMetaDataThunk(
  "productMetaData/updateProductMetaData",
  updateProductMetaDataAPI,
);

export const deleteProductMetaData = createProductMetaDataThunk(
  "productMetaData/deleteProductMetaData",
  deleteProductMetaDataAPI,
);

export const uploadProductMetaDataIcon = createProductMetaDataThunk(
  "productMetaData/uploadProductMetaDataIcon",
  uploadProductMetaDataIconAPI,
);

export const deleteProductMetaDataIcon = createProductMetaDataThunk(
  "productMetaData/deleteProductMetaDataIcon",
  deleteProductMetaDataIconAPI,
);

const getResponse = (payload) => payload?.response ?? payload;

const setPending = (state, key) => {
  state[key] = true;
  state.error = null;
  state.success = false;
};

const setRejected = (state, action, key) => {
  state[key] = false;
  state.error = action?.payload?.errorMessage || null;
  state.success = false;
};

const productMetaDataSlice = createSlice({
  name: "productMetaData",
  initialState,
  reducers: {
    clearProductMetaDataError: (state) => {
      state.error = null;
    },
    clearProductMetaDataIcon: (state) => {
      state.uploadedIcon = null;
    },
    clearProductMetaDataState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductMetaData.pending, (state) => {
        setPending(state, "detailsLoading");
      })
      .addCase(getProductMetaData.fulfilled, (state, action) => {
        state.productMetaData = getResponse(action.payload);
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getProductMetaData.rejected, (state, action) => {
        setRejected(state, action, "detailsLoading");
      })
      .addCase(getAllProductMetaData.pending, (state) => {
        setPending(state, "loading");
      })
      .addCase(getAllProductMetaData.fulfilled, (state, action) => {
        state.productMetaDataList = getResponse(action.payload) ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllProductMetaData.rejected, (state, action) => {
        setRejected(state, action, "loading");
      })
      .addCase(createProductMetaData.pending, (state) => {
        setPending(state, "saving");
      })
      .addCase(createProductMetaData.fulfilled, (state, action) => {
        state.productMetaData = getResponse(action.payload);
        state.saving = false;
        state.success = true;
      })
      .addCase(createProductMetaData.rejected, (state, action) => {
        setRejected(state, action, "saving");
      })
      .addCase(updateProductMetaData.pending, (state) => {
        setPending(state, "saving");
      })
      .addCase(updateProductMetaData.fulfilled, (state, action) => {
        state.productMetaData = getResponse(action.payload);
        state.saving = false;
        state.success = true;
      })
      .addCase(updateProductMetaData.rejected, (state, action) => {
        setRejected(state, action, "saving");
      })
      .addCase(deleteProductMetaData.pending, (state) => {
        setPending(state, "deleting");
      })
      .addCase(deleteProductMetaData.fulfilled, (state) => {
        state.productMetaData = null;
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteProductMetaData.rejected, (state, action) => {
        setRejected(state, action, "deleting");
      })
      .addCase(uploadProductMetaDataIcon.pending, (state) => {
        setPending(state, "iconUploading");
      })
      .addCase(uploadProductMetaDataIcon.fulfilled, (state, action) => {
        state.uploadedIcon = getResponse(action.payload);
        state.iconUploading = false;
        state.success = true;
      })
      .addCase(uploadProductMetaDataIcon.rejected, (state, action) => {
        setRejected(state, action, "iconUploading");
      })
      .addCase(deleteProductMetaDataIcon.pending, (state) => {
        setPending(state, "iconDeleting");
      })
      .addCase(deleteProductMetaDataIcon.fulfilled, (state) => {
        state.uploadedIcon = null;
        state.iconDeleting = false;
        state.success = true;
      })
      .addCase(deleteProductMetaDataIcon.rejected, (state, action) => {
        setRejected(state, action, "iconDeleting");
      });
  },
});

export const {
  clearProductMetaDataError,
  clearProductMetaDataIcon,
  clearProductMetaDataState,
} = productMetaDataSlice.actions;

export default productMetaDataSlice.reducer;
